/**
 * The `/search` view: a results panel beside the search agent's chat
 * (app-shell ADR-021). The user asks in words; the agent chooses
 * the scope, entity types and mode and calls the `search` tool; this panel
 * shows what that call returned.
 *
 * There is no query input, no entity-type chip, no mode selector and no sort
 * control — those asked the user to express intent as filter state, and they
 * would be a second source of truth for what is in this list.
 *
 * The chat itself is the shell panel, docked `right` by this route's handle.
 *
 * Lives under `app-shell/src/views/search/` so both the web and the
 * desktop app pick it up via the shared shell.
 */

import { useEffect, useMemo, useState } from 'react';
import { useAtomValue } from 'jotai';
import type { SearchResult } from '@localagent/sdk';
import { holdsIdentityAtom } from '../../stores/auth/atoms';
import { sdkConfiguredAtom } from '../../stores/sdk/atoms';
import { getSDK } from '../../services/sdk-instance';
import { usePublishViewSnapshot } from '../../components/shogun/usePublishViewSnapshot';
import { ResultRow } from './ResultRow';
import { useAgentSearchResults } from './useAgentSearchResults';
import { PageHeaderBar } from '../../components/layout/PageHeaderBar';

const STARTER_PROMPTS = [
  'What have I been working on recently?',
  'Find the tickets about authentication',
  'Where did we write down the retry policy?',
];

export function SearchView() {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);
  const { results, isSearching, error, awaitingFirstSearch } = useAgentSearchResults();
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Before the agent has searched, the panel shows what the account has been
  // touching. It is not a filter and does not compete with the agent — it is
  // what the screen has to say before a question has been asked.
  const [recent, setRecent] = useState<SearchResult[]>([]);
  const [recentError, setRecentError] = useState<string | null>(null);
  useEffect(() => {
    if (!isAuthenticated || !sdkConfigured || !awaitingFirstSearch) return;
    let cancelled = false;
    void getSDK()
      .search.recent()
      .then((response) => {
        if (!cancelled) setRecent(response.results);
      })
      .catch((err: unknown) => {
        if (!cancelled) setRecentError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, sdkConfigured, awaitingFirstSearch]);

  const showingRecent = awaitingFirstSearch;
  const rows = showingRecent ? recent : results;

  useEffect(() => {
    setSelectedIndex(0);
  }, [rows.length, showingRecent]);

  // Tell the agent what this screen is showing, so "the second one" resolves
  // against the same list the user is looking at (app-shell ADR-014).
  const snapshot = useMemo(
    () => ({
      route: '/search',
      context: {
        surface: 'search',
        primary_type: 'search_result',
        counts: [{ lane: showingRecent ? 'recent' : 'results', total: rows.length }],
        visible: rows.map((r) => ({
          type: r.entity_type,
          id: r.entity_id,
          label: r.title,
          state: showingRecent ? 'recent' : 'result',
        })),
      },
      ...(rows.length === 0 ? { starterPrompts: STARTER_PROMPTS } : {}),
    }),
    [rows, showingRecent],
  );
  usePublishViewSnapshot(snapshot);

  const shownError = showingRecent ? recentError : error;

  return (
    <div className="flex h-full flex-col" data-testid="search-view">
      <PageHeaderBar
        description={
          showingRecent ? 'Recent activity' : `${rows.length} result${rows.length === 1 ? '' : 's'}`
        }
      />

      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-4xl p-4">
          {shownError && (
            <div
              className="rounded-md bg-destructive/10 p-3 text-body text-destructive"
              data-testid="search-error"
              role="alert"
            >
              {shownError}
            </div>
          )}
          {!shownError && isSearching && (
            <div
              className="py-8 text-center text-body text-muted-foreground"
              data-testid="search-loading"
              role="status"
            >
              Searching…
            </div>
          )}
          {!shownError && !isSearching && rows.length === 0 && (
            <div
              className="py-8 text-center text-body text-muted-foreground"
              data-testid="search-empty"
            >
              {showingRecent
                ? 'No recent activity yet. Ask the search agent to find something.'
                : 'That search found nothing.'}
            </div>
          )}
          <div
            role="listbox"
            aria-label="Search results"
            data-testid="search-results"
            data-recent={showingRecent ? 'true' : 'false'}
            className="flex flex-col gap-1"
          >
            {rows.map((r, i) => (
              <ResultRow
                key={`${r.entity_type}:${r.entity_id}:${i}`}
                result={r}
                isSelected={i === selectedIndex}
                onSelect={() => setSelectedIndex(i)}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
